import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Package, Clock, ArrowClockwise, CheckCircle, WarningCircle } from '@phosphor-icons/react';

const STATUS_LABELS = {
  demande_envoyee: 'Demande envoyée',
  en_cours_analyse: 'En cours d\'analyse',
  en_attente_informations: 'En attente d\'infos',
  solution_validee: 'Solution validée',
  en_attente_retour: 'Attente retour',
  produit_recu: 'Produit reçu',
  en_fabrication: 'En fabrication',
  expediee: 'Expédiée',
  cloturee: 'Clôturée'
};

const STATUS_COLORS = {
  demande_envoyee: 'bg-blue-100 text-blue-800',
  en_cours_analyse: 'bg-amber-100 text-amber-800',
  en_attente_informations: 'bg-orange-100 text-orange-800',
  solution_validee: 'bg-emerald-100 text-emerald-800',
  en_attente_retour: 'bg-purple-100 text-purple-800',
  produit_recu: 'bg-indigo-100 text-indigo-800',
  en_fabrication: 'bg-yellow-100 text-yellow-800',
  expediee: 'bg-teal-100 text-teal-800',
  cloturee: 'bg-zinc-100 text-zinc-600'
};

const DECISION_LABELS = {
  refabrication_gratuite: 'Refabrication gratuite',
  refabrication_payante: 'Refabrication payante',
  geste_commercial: 'Geste commercial'
};

export default function ClientSavList() {
  const [savList, setSavList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSav();
  }, []);

  const fetchSav = async () => {
    try {
      const res = await axios.get(`${API}/sav/mine`);
      setSavList(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  if (loading) return <div className="p-8 text-center text-zinc-500">Chargement des demandes SAV...</div>;

  if (savList.length === 0) return null;

  return (
    <div className="bg-white border border-zinc-200 mt-6" data-testid="client-sav-list">
      <div className="p-4 sm:p-6 border-b border-zinc-200 flex items-center gap-2">
        <WarningCircle size={20} className="text-amber-500" />
        <h2 className="font-heading font-semibold text-base sm:text-lg">Mes demandes SAV</h2>
        <span className="ml-auto text-xs text-zinc-400">{savList.length} demande{savList.length > 1 ? 's' : ''}</span>
      </div>

      <div className="divide-y divide-zinc-100">
        {savList.map(sav => (
          <div key={sav.id} className="p-4 sm:p-5 hover:bg-zinc-50 transition-colors" data-testid={`sav-item-${sav.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono font-bold text-sm" data-testid={`sav-number-${sav.id}`}>{sav.sav_number}</span>
                  {sav.urgency === 'urgent' && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-sm">Urgent</span>
                  )}
                </div>
                <p className="text-sm text-zinc-600 truncate">{sav.product_name} — {sav.order_patient_ref}</p>
                <p className="text-xs text-zinc-400 mt-0.5">
                  {sav.request_type?.replace(/_/g, ' ')} — {formatDate(sav.created_at)}
                </p>
                {sav.decision && (
                  <p className="text-xs mt-1 font-medium text-emerald-700">
                    {DECISION_LABELS[sav.decision.type] || sav.decision.type}
                  </p>
                )}
              </div>
              <span className={`text-[11px] font-medium px-2 py-1 rounded-sm whitespace-nowrap ${STATUS_COLORS[sav.status] || 'bg-zinc-100 text-zinc-600'}`}>
                {STATUS_LABELS[sav.status] || sav.status}
              </span>
            </div>

            {/* Status timeline */}
            {sav.status_history && sav.status_history.length > 1 && (
              <div className="mt-3 pl-3 border-l-2 border-zinc-200 space-y-1">
                {sav.status_history.slice(-3).map((h, i) => (
                  <div key={`${h.changed_at}-${h.status}`} className="text-xs text-zinc-400">
                    <span className="font-medium text-zinc-600">{STATUS_LABELS[h.status] || h.status}</span>
                    {' — '}{formatDate(h.changed_at)}
                    {h.comment && <span className="italic"> — {h.comment}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
